import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import SectionHeader from '../../../../Components/GeneralComponents/SectionHeader'
import { useMyContext } from '../../../../Components/ContextProviders/CreditsContextProvider'
import { getTeamUuid } from '../../../../utils/cookies'
import { updateCredits } from '../../../../Requests/TeamRequests'
import SavedArticlesTable from '../../../../Components/ArticleBibliography/SavedArticlesTable'
import ArticleSearchForm from '../../../../Components/ArticleBibliography/ArticleSearchForm'
import SearchResultsList from '../../../../Components/ArticleBibliography/SearchResultsList'
import SearchHistory from '../../../../Components/ArticleBibliography/SearchHistory'
import { getSavedArticles } from '../../../../Requests/ArticleBibliographyRequests'
import { useSidebar } from '../../../../Components/ContextProviders/SidebarContextProvider'
import { getSidebarPadding } from '../../../../utils/sidebar'

function ArticleBibliography() {
    const { t } = useTranslation()
    const { project_uuid } = useParams()
    const { credits, setCredits } = useMyContext()
    const { isCollapsed } = useSidebar()
    
    const [savedArticles, setSavedArticles] = useState([])
    const [savedArticlesLoading, setSavedArticlesLoading] = useState(true)
    const [savedArticlesPagination, setSavedArticlesPagination] = useState(null)
    const [currentSearch, setCurrentSearch] = useState(null)
    const [searchResults, setSearchResults] = useState([])
    const [searchLoading, setSearchLoading] = useState(false)
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    useEffect(() => {
        loadSavedArticles()
        updateCreditsData()
    }, [project_uuid, refreshTrigger])

    const loadSavedArticles = async (page = 1) => {
        try {
            setSavedArticlesLoading(true)
            const response = await getSavedArticles(getTeamUuid(), project_uuid, page)
            setSavedArticles(response.data.data)
            setSavedArticlesPagination({
                current_page: response.data.current_page,
                last_page: response.data.last_page,
                per_page: response.data.per_page,
                total: response.data.total
            })
        } catch (error) {
            console.error('Error loading saved articles:', error)
            setSavedArticles([])
        } finally {
            setSavedArticlesLoading(false)
        }
    }

    const updateCreditsData = async () => {
        try {
            const response = await updateCredits(getTeamUuid())
            setCredits(response.data)
        } catch (error) {
            console.error('Error updating credits:', error)
        }
    }

    const handleArticleSaved = () => {
        setRefreshTrigger(prev => prev + 1)
        updateCreditsData()
    }

    const handleArticleDeleted = () => {
        setRefreshTrigger(prev => prev + 1)
    }

    const handleSearchComplete = (searchData, results) => {
        setCurrentSearch(searchData)
        setSearchResults(results)
        updateCreditsData()
    }

    return (
        <div className={`${getSidebarPadding(isCollapsed)} transition-all duration-300 pt-4 md:pt-12 w-full flex justify-center`}>
            <div className='flex flex-col w-full justify-center items-center px-4 space-y-8'>
                {/* Saved Articles Section */}
                <div className='bg-white max-w-7xl rounded-2xl w-full shadow-xl'>
                    <SectionHeader 
                        title={t('articleBibliography.savedArticles.title')} 
                        subtitle={t('articleBibliography.savedArticles.subtitle')}
                    />
                    <div className="px-4 py-5 sm:px-6">
                        <SavedArticlesTable
                            articles={savedArticles}
                            loading={savedArticlesLoading}
                            pagination={savedArticlesPagination}
                            onPageChange={loadSavedArticles}
                            onArticleDeleted={handleArticleDeleted}
                        />
                    </div>
                </div>

                {/* Article Search Section */}
                <div className='bg-white max-w-7xl rounded-2xl w-full shadow-xl'>
                    <SectionHeader 
                        title={t('articleBibliography.search.title')} 
                        subtitle={t('articleBibliography.search.subtitle')}
                    />
                    <div className="px-4 py-5 sm:px-6 space-y-6">
                        <ArticleSearchForm
                            credits={credits}
                            onSearchComplete={handleSearchComplete}
                            loading={searchLoading}
                            setLoading={setSearchLoading}
                        />
                        
                        {(currentSearch || searchResults.length > 0) && (
                            <SearchResultsList
                                currentSearch={currentSearch}
                                results={searchResults}
                                projectId={project_uuid}
                                onArticleSaved={handleArticleSaved}
                                loading={searchLoading}
                            />
                        )}
                    </div>
                </div>

                {/* Search History Section */}
                <div className='bg-white max-w-7xl rounded-2xl w-full shadow-xl'>
                    <div className="px-4 py-5 sm:px-6">
                        <SearchHistory onArticleSaved={handleArticleSaved} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ArticleBibliography